<template>
    <div class="empty-container">
        <div class="empty">
            <div class="image-container">
                <img :src="emptyImage" :alt="header" />
            </div>
            <p class="heading mb-0 py-2">{{ header }}</p>
            <p :class="masterEmptyContent ? 'content mb-0' : 'bill-content mb-0'">{{ message }}</p>

            <div v-if="masterEmptyContent && showCreateLine" class="create pt-3" @click="$emit('createNew')">
                <span><i class="pi pi-plus plus-icon"></i></span>
                <span class="add-text ps-2">{{ createLabel }}</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

/*
  `content` used to mean two different things: the sentence under the heading in
  one branch, and the bare noun inside "Create a new …" in the other. Every
  caller passed it as the sentence, so that is what it means now, in both
  branches, and the button carries its own wording in `createLabel`.

  The sentence is still generated from the header when a caller gives none, so
  a screen that passes nothing keeps working.
*/
const props = defineProps({
    header: {
        type: String,
        default: ''
    },
    content: {
        type: String,
        default: ''
    },
    emptyImage: {
        type: String,
        default: ''
    },
    createLabel: {
        type: String,
        default: 'Create a new one'
    },
    route: {
        type: String,
        default: ''
    },
    showCreateLine: {
        type: Boolean,
        default: true
    },
    masterEmptyContent: {
        type: Boolean,
        default: true
    }
})

defineEmits(['createNew'])

const message = computed(() =>
    props.content || (props.header ? `Your ${props.header} list is empty right now.` : 'Nothing here yet.'))
</script>

<style lang="scss" scoped>
.empty-container {
    @apply bg-gray-50 h-[60vh] w-full flex justify-center items-center;

    .empty {
        .image-container {
            @apply text-center block mx-auto w-[50px];

            img {
                @apply w-[50px];
            }
        }

        .heading {
            @apply text-gray-700 text-center font-semibold text-base tracking-wider;
        }

        .content {
            @apply text-center text-gray-400 text-xs mb-2.5;
        }

        .bill-content {
            @apply text-center text-gray-400 text-sm mb-2.5;
        }

        .create {
            @apply cursor-pointer flex justify-center font-medium text-primary text-sm;

            .plus-icon {
                @apply text-xs;
            }

            .add-text {
                @apply text-sm;
            }
        }
    }
}
</style>
